import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronRight, DollarSign, Info, Inbox, RotateCcw } from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useI18n } from '@/contexts/I18nContext'
import { useStripeConnect } from '@/hooks/useStripeConnect'
import { useRollReviewSettings } from '../hooks/useRollReviewSettings'
import { useRollReviewPricing } from '../hooks/useRollReviewPricing'
import {
  CURRENCY_SYMBOL,
  STRIPE_MINIMUMS,
  formatStripeMinimum,
  type RollReviewCurrency,
  type TierKey,
} from '../domain/rollReviewPricing'
import { SOSSIETY_RED } from '@/styles/brand'

// ─── Design tokens (match InstructorTools) ────────────────────────────────────

const C = {
  border: '#FFFFFF0D',
  text:   '#FFFFFF',
  muted:  '#8D8D8D',
  accent: SOSSIETY_RED,
  red:    SOSSIETY_RED,
}

const CURRENCIES: RollReviewCurrency[] = ['USD', 'BRL', 'EUR']

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionTitle = ({
  children,
  dimmed = false,
}: {
  children: React.ReactNode
  dimmed?: boolean
}) => (
  <p
    className="px-5 text-xs font-semibold tracking-widest pt-6 pb-2"
    style={{ color: dimmed ? 'rgba(141,141,141,0.4)' : C.muted }}
  >
    {children}
  </p>
)

// ─── Orientation Banner (Scenario 4A) ─────────────────────────────────────────

const OrientationBanner = () => {
  const { t } = useI18n()
  return (
    <div
      className="mx-5 mt-4 rounded-[14px] border flex items-start gap-3 px-4 py-3.5"
      style={{ backgroundColor: 'rgba(215,46,22,0.08)', borderColor: 'rgba(215,46,22,0.25)' }}
    >
      <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: C.accent }} />
      <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
        {t('roll_review_pricing.orientation_banner')}
      </p>
    </div>
  )
}

// ─── NavRow ───────────────────────────────────────────────────────────────────

const NavRow = ({
  icon: Icon,
  label,
  value,
  onClick,
  dimmed = false,
}: {
  icon: React.ElementType
  label: string
  value?: string
  onClick?: () => void
  dimmed?: boolean
}) => (
  <button
    onClick={onClick}
    disabled={!onClick}
    className="w-full flex items-center gap-4 px-5 py-4 border-b text-left active:opacity-70 transition-opacity disabled:cursor-default"
    style={{ borderColor: C.border, opacity: dimmed ? 0.4 : 1 }}
  >
    <Icon className="w-5 h-5 flex-shrink-0" style={{ color: C.muted }} />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-white">{label}</p>
    </div>
    {value && (
      <span className="text-sm text-white/50 mr-1">{value}</span>
    )}
    {onClick && <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: C.muted }} />}
  </button>
)

// ─── ToggleRow ────────────────────────────────────────────────────────────────

const ToggleRow = ({
  label,
  subtitle,
  checked,
  onCheckedChange,
  disabled = false,
  dimmed = false,
}: {
  label: string
  subtitle?: string
  checked: boolean
  onCheckedChange: (v: boolean) => void
  disabled?: boolean
  dimmed?: boolean
}) => (
  <div
    className="flex items-center justify-between px-5 py-4 border-b transition-opacity"
    style={{ borderColor: C.border, opacity: dimmed ? 0.4 : 1 }}
  >
    <div className="flex-1 pr-4">
      <p className="text-sm font-medium text-white">{label}</p>
      {subtitle && (
        <p className="text-xs mt-0.5" style={{ color: C.muted }}>{subtitle}</p>
      )}
    </div>
    <Switch
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
    />
  </div>
)

// ─── HourlyRateRow ────────────────────────────────────────────────────────────

const HourlyRateRow = ({
  currency,
  hourlyRateCents,
  onCommit,
  dimmed = false,
}: {
  currency: RollReviewCurrency
  hourlyRateCents: number | null
  onCommit: (cents: number | null) => void
  dimmed?: boolean
}) => {
  const { t }        = useI18n()
  const [editing, setEditing] = useState(false)
  const inputRef     = useRef<HTMLInputElement>(null)

  const displayValue = hourlyRateCents
    ? `${CURRENCY_SYMBOL[currency]} ${(hourlyRateCents / 100).toFixed(0)} / h`
    : t('roll_review_pricing.hourly_rate_placeholder')

  const handleBlur = () => {
    const raw = parseFloat(inputRef.current?.value ?? '0')
    const cents = isNaN(raw) || raw <= 0 ? null : Math.round(raw * 100)
    onCommit(cents)
    setEditing(false)
  }

  return (
    <div
      className="flex items-center justify-between px-5 py-4 border-b transition-opacity"
      style={{ borderColor: C.border, opacity: dimmed ? 0.4 : 1 }}
    >
      <p className="text-sm font-medium text-white">
        {t('roll_review_pricing.hourly_rate_label')}
      </p>
      {editing ? (
        <input
          ref={inputRef}
          type="number"
          min={0}
          step={1}
          defaultValue={hourlyRateCents ? hourlyRateCents / 100 : ''}
          autoFocus
          className="w-28 text-right text-sm bg-transparent text-white border-b border-white/30 outline-none"
          onBlur={handleBlur}
          onKeyDown={e => e.key === 'Enter' && handleBlur()}
        />
      ) : (
        <button
          onClick={() => !dimmed && setEditing(true)}
          className="text-sm text-white/60 active:text-white transition-colors"
        >
          {displayValue}
        </button>
      )}
    </div>
  )
}

// ─── TierRow ──────────────────────────────────────────────────────────────────

const TierRow = ({
  tierKey,
  label,
  priceCents,
  isManual,
  currency,
  hasError,
  errorMessage,
  onCommit,
  onReset,
  dimmed = false,
}: {
  tierKey:      TierKey
  label:        string
  priceCents:   number
  isManual:     boolean
  currency:     RollReviewCurrency
  hasError:     boolean
  errorMessage: string | null
  onCommit:     (cents: number) => void
  onReset:      () => void
  dimmed?:      boolean
}) => {
  const [editing, setEditing]   = useState(false)
  const inputRef                = useRef<HTMLInputElement>(null)
  const symbol                  = CURRENCY_SYMBOL[currency]

  const displayValue = priceCents > 0
    ? `${symbol} ${(priceCents / 100).toFixed(2)}`
    : '—'

  const handleBlur = () => {
    const raw   = parseFloat(inputRef.current?.value ?? '0')
    const cents = isNaN(raw) || raw < 0 ? 0 : Math.round(raw * 100)
    onCommit(cents)
    setEditing(false)
  }

  return (
    <div
      className="transition-opacity"
      style={{ opacity: dimmed ? 0.4 : 1 }}
    >
      <div
        className="flex items-center px-5 py-3.5 border-b"
        style={{
          borderColor: hasError ? 'rgba(215,46,22,0.6)' : C.border,
        }}
      >
        {/* Label */}
        <p className="flex-1 text-sm text-white/80">{label}</p>

        {/* Reset to auto (only when manual) */}
        {isManual && !editing && (
          <button
            onClick={() => !dimmed && onReset()}
            className="mr-3 p-1 active:opacity-60 transition-opacity"
            title="Reset to auto"
          >
            <RotateCcw className="w-3.5 h-3.5" style={{ color: C.muted }} />
          </button>
        )}

        {/* Price input or display */}
        {editing ? (
          <input
            ref={inputRef}
            type="number"
            min={0}
            step={0.01}
            defaultValue={priceCents > 0 ? priceCents / 100 : ''}
            autoFocus
            className="w-24 text-right text-sm bg-transparent border-b border-white/30 outline-none"
            style={{ color: C.text }}
            onBlur={handleBlur}
            onKeyDown={e => e.key === 'Enter' && handleBlur()}
          />
        ) : (
          <button
            onClick={() => !dimmed && setEditing(true)}
            className="text-sm font-medium transition-colors active:opacity-70"
            style={{ color: isManual ? C.red : 'rgba(255,255,255,0.5)' }}
          >
            {displayValue}
          </button>
        )}
      </div>

      {/* Stripe validation error */}
      {hasError && errorMessage && (
        <p className="px-5 pt-1 pb-2 text-[11px]" style={{ color: C.red }}>
          {errorMessage}
        </p>
      )}
    </div>
  )
}

// ─── UnsavedChangesBar ────────────────────────────────────────────────────────

const UnsavedChangesBar = ({
  visible,
  isSaving,
  canSave,
  onDiscard,
  onSave,
}: {
  visible:  boolean
  isSaving: boolean
  canSave:  boolean
  onDiscard: () => void
  onSave:    () => void
}) => {
  const { t } = useI18n()

  return (
    <div
      className={`fixed bottom-0 inset-x-0 z-40 bg-sossiety-dark transition-transform duration-300 ${
        visible ? 'translate-y-0' : 'translate-y-full'
      }`}
    >
      {/* Fade edge blends into scrollable content above */}
      <div className="h-6 bg-gradient-to-b from-transparent to-sossiety-dark -mt-6 pointer-events-none" />

      <div className="px-4 pb-8 pt-2 flex flex-col gap-3">
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          onClick={onSave}
          disabled={!canSave || isSaving}
        >
          {isSaving
            ? t('roll_review_pricing.saving')
            : t('roll_review_pricing.save')}
        </Button>
        <Button
          variant="outline"
          size="lg"
          className="w-full border-white/20"
          onClick={onDiscard}
          disabled={isSaving}
        >
          {t('roll_review_pricing.discard')}
        </Button>
      </div>
    </div>
  )
}

// ─── Currency Sheet ───────────────────────────────────────────────────────────

const CurrencySheet = ({
  open,
  current,
  onSelect,
  onClose,
}: {
  open:     boolean
  current:  RollReviewCurrency
  onSelect: (c: RollReviewCurrency) => void
  onClose:  () => void
}) => {
  const { t } = useI18n()
  return (
    <Sheet open={open} onOpenChange={v => !v && onClose()}>
      <SheetContent side="bottom" className="bg-sossiety-surface border-0 rounded-t-[20px] pb-safe">
        <SheetHeader className="mb-2">
          <SheetTitle className="text-white text-sm">
            {t('roll_review_pricing.select_currency')}
          </SheetTitle>
        </SheetHeader>
        <div className="flex flex-col">
          {CURRENCIES.map(c => (
            <button
              key={c}
              onClick={() => { onSelect(c); onClose() }}
              className="flex items-center justify-between px-4 py-4 border-b active:bg-white/5 transition-colors"
              style={{ borderColor: C.border }}
            >
              <span className="text-sm font-medium text-white">
                {CURRENCY_SYMBOL[c]} {c}
              </span>
              {c === current && (
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: C.accent }} />
              )}
            </button>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  )
}

// ─── Main tab ─────────────────────────────────────────────────────────────────

export const RollReviewPricingTab: React.FC = () => {
  const { t }      = useI18n()
  const navigate   = useNavigate()

  const { account: stripeAccount, isLoading: stripeLoading } = useStripeConnect()
  const { acceptRollReviews, isLoading: settingsLoading, setAcceptRollReviews, isUpdating: settingsUpdating } =
    useRollReviewSettings()

  const {
    draft,
    isLoading:         pricingLoading,
    isSaving,
    isFirstAccess,
    hasUnsavedChanges,
    validationErrors,
    setCurrency,
    setUseHourlyBase,
    setHourlyRate,
    setTierPrice,
    resetTierToAuto,
    save,
    discard,
  } = useRollReviewPricing()

  const [currencySheetOpen, setCurrencySheetOpen] = useState(false)

  const isStripeOnboarded = !stripeLoading &&
    stripeAccount?.charges_enabled &&
    stripeAccount?.payouts_enabled

  const isLoading = pricingLoading || settingsLoading || stripeLoading

  // Scenario 4D: accept is OFF — dim the pricing config but keep it readable
  const isDimmed = !acceptRollReviews

  if (isLoading || !draft) {
    return (
      <div className="px-5 pt-10 flex flex-col gap-3">
        {[1, 2, 3, 4].map(i => (
          <div
            key={i}
            className="h-14 rounded-xl animate-pulse"
            className="bg-sossiety-overlay rounded-xl"
          />
        ))}
      </div>
    )
  }

  const canSave = hasUnsavedChanges && Object.keys(validationErrors).length === 0

  return (
    <>
      {/* ── Scenario 4A: orientation banner (first access) ── */}
      {isFirstAccess && <OrientationBanner />}

      {/* ── Currency ── */}
      <SectionTitle dimmed={isDimmed}>
        {t('roll_review_pricing.section_currency').toUpperCase()}
      </SectionTitle>
      <NavRow
        icon={DollarSign}
        label={t('roll_review_pricing.currency_label')}
        value={`${CURRENCY_SYMBOL[draft.currency]} ${draft.currency}`}
        onClick={isDimmed ? undefined : () => setCurrencySheetOpen(true)}
        dimmed={isDimmed}
      />

      {/* ── Hourly base toggle ── */}
      <SectionTitle dimmed={isDimmed}>
        {t('roll_review_pricing.section_base').toUpperCase()}
      </SectionTitle>
      <ToggleRow
        label={t('roll_review_pricing.use_hourly_base')}
        subtitle={t('roll_review_pricing.use_hourly_base_subtitle')}
        checked={draft.useHourlyBase}
        onCheckedChange={setUseHourlyBase}
        disabled={isDimmed}
        dimmed={isDimmed}
      />

      {/* ── Hourly rate input (only when toggle is ON) ── */}
      {draft.useHourlyBase && (
        <HourlyRateRow
          currency={draft.currency}
          hourlyRateCents={draft.hourlyRateCents}
          onCommit={setHourlyRate}
          dimmed={isDimmed}
        />
      )}

      {/* ── Tier pricing ── */}
      <SectionTitle dimmed={isDimmed}>
        {t('roll_review_pricing.section_tiers').toUpperCase()}
      </SectionTitle>
      {draft.tiers.map(tier => {
        const errKey     = validationErrors[tier.key]
        const min        = STRIPE_MINIMUMS[draft.currency]
        const errMessage = errKey
          ? t('roll_review_pricing.error_stripe_min', {
              min: formatStripeMinimum(draft.currency),
              currency: draft.currency,
            })
          : null

        return (
          <TierRow
            key={tier.key}
            tierKey={tier.key}
            label={tier.label}
            priceCents={tier.priceCents}
            isManual={tier.isManual}
            currency={draft.currency}
            hasError={!!errKey}
            errorMessage={errMessage}
            onCommit={cents => setTierPrice(tier.key, cents)}
            onReset={() => resetTierToAuto(tier.key)}
            dimmed={isDimmed}
          />
        )
      })}

      {/* ── Accept Roll Reviews toggle (Scenarios 4C, 4D) ── */}
      <SectionTitle>
        {t('roll_review_pricing.section_accept').toUpperCase()}
      </SectionTitle>

      {/* Scenario 4C: no Stripe — show CTA instead of toggle */}
      {!stripeLoading && !isStripeOnboarded ? (
        <button
          onClick={() => navigate('/settings/instructor-tools/earnings')}
          className="w-full flex items-center gap-4 px-5 py-4 border-b text-left active:opacity-70 transition-opacity"
          style={{ borderColor: C.border }}
        >
          <DollarSign className="w-5 h-5 flex-shrink-0" style={{ color: C.muted }} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white">
              {t('roll_review_pricing.stripe_cta')}
            </p>
            <p className="text-xs mt-0.5" style={{ color: C.muted }}>
              {t('roll_review_pricing.stripe_cta_subtitle')}
            </p>
          </div>
          <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: C.muted }} />
        </button>
      ) : (
        <ToggleRow
          label={t('instructor_tools.accept_roll_reviews')}
          subtitle={
            hasUnsavedChanges
              ? t('roll_review_pricing.save_first_hint')
              : t('instructor_tools.accept_roll_reviews_subtitle')
          }
          checked={acceptRollReviews}
          onCheckedChange={setAcceptRollReviews}
          // Disabled if: no Stripe, or there are unsaved changes (force save first)
          disabled={settingsLoading || settingsUpdating || !isStripeOnboarded || hasUnsavedChanges}
        />
      )}

      {/* Review inbox nav row */}
      <NavRow
        icon={Inbox}
        label={t('instructor_tools.review_inbox')}
        subtitle={t('instructor_tools.review_inbox_subtitle')}
        onClick={() => navigate('/my-roll-reviews')}
      />

      {/* ── Sticky save bar (Scenario 3C, 4B) ── */}
      <UnsavedChangesBar
        visible={hasUnsavedChanges}
        isSaving={isSaving}
        canSave={canSave}
        onDiscard={discard}
        onSave={save}
      />

      {/* extra bottom padding so save bar doesn't overlap last item */}
      {hasUnsavedChanges && <div className="h-40" />}

      {/* ── Currency bottom sheet ── */}
      <CurrencySheet
        open={currencySheetOpen}
        current={draft.currency}
        onSelect={setCurrency}
        onClose={() => setCurrencySheetOpen(false)}
      />
    </>
  )
}
